// Bridge to the Android BluetoothPrinterPlugin (android/.../BluetoothPrinterPlugin.java).
// Classic Bluetooth (RFCOMM/SPP) thermal printers can't be reached from the
// WebView, so on the native app the Bluetooth printer transport goes through
// this plugin. Printers are paired once in Android's system Bluetooth settings;
// here we list those bonded devices and stream raw ESC/POS bytes to one.

import { registerPlugin } from '@capacitor/core';
import { isNativeApp } from './nativeUsb';

export interface BtDevice {
  name: string | null;
  address: string;
}

interface BluetoothPrinterNativePlugin {
  ensurePermissions(): Promise<{ granted: boolean }>;
  isEnabled(): Promise<{ enabled: boolean }>;
  list(): Promise<{ enabled: boolean; devices: BtDevice[] }>;
  print(opts: { address: string; data: string }): Promise<void>;
}

const BluetoothPrinterNative = registerPlugin<BluetoothPrinterNativePlugin>('BluetoothPrinter');

export { isNativeApp };

// Triggers the Android 12+ "Allow Bluetooth?" system dialog (resolves true
// immediately on older versions, where the permission is granted at install).
export async function btEnsurePermissions(): Promise<boolean> {
  const { granted } = await BluetoothPrinterNative.ensurePermissions();
  return granted;
}

export async function btIsEnabled(): Promise<boolean> {
  const { enabled } = await BluetoothPrinterNative.isEnabled();
  return enabled;
}

export async function btListDevices(): Promise<{ enabled: boolean; devices: BtDevice[] }> {
  return BluetoothPrinterNative.list();
}

export async function btPrint(address: string, data: Uint8Array): Promise<void> {
  await BluetoothPrinterNative.print({ address, data: toBase64(data) });
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000; // String.fromCharCode arg limit safety
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return btoa(binary);
}
